import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { getFirestore, query, getDocs, doc, collection, limit, where, orderBy, startAfter, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore'

/**
 * Esta es la configuración por proyecto
*/
const app = initializeApp({
    apiKey: "AIzaSyDeZLTXQ5bABR3y4RmLcoXByzfM8qnTJE4",
    authDomain: "proassa-15ec1.firebaseapp.com",
    projectId: "proassa-15ec1",
    storageBucket: "proassa-15ec1.appspot.com",
    messagingSenderId: "924184849605",
    appId: "1:924184849605:web:eac063b712922991cd14ac",
    measurementId: "G-YM36ZWSW37"
});

/**
 * Desautenticar la aplicacion
 */
const logOut = async () => {
    const au = getAuth(app);
    await signOut(au);
}

/**
 * Obtiene token de usuario autenticado que es lo que devuelve
 * @returns token
 */
const getBearer = () => {
    return getAuth(app).currentUser.getIdToken(true);
}

/**
 * Extrae el pathPart de la url para poder identificar la compañia de la que se trata. Indispensable para soportar multicompañia
 * Preparado para extraer la primera parte del host <compania>.<dominio> Ejemplo: proassa.newba.cloud
 * @param {String} s    = location / host de la pagina en concreto
 * @param {Number} idx  = indice del valor que queremos extraer del host
 * @returns 
 */
const pathPart = (s = window.location.host, idx = 1) => {
    // NEWBA DEMO = encuestas.newba.cloud
    // PROASSA =    proassa.newba.cloud
    // HOTELES/ALOJAMIENTOS = lodge.newba.cloud

    var i = idx;
    var ret = '';
    try {
        ret = s.split(".")[i];
        if (s === 'localhost:3000') ret = 'newba'; // este if para q aparezca logos en localhost (complementa a newba.wombat.com)
        if (ret === 'proassa-web') ret = 'newba'; // el hosting por defecto nos redirigirá a newba
        if (ret === 'encuestas') ret = 'newba';     // esta será la de newba demo, la de por defecto.  
    }
    catch (e) { console.error(`pathPart error ${e.toString()}`); }
    return ret;
}

/**
 * Permite realizar las distintas consultas a Firestore para obtener los datos deseados según los parámetros recibidos.
 * Por ejemplo, los parámetros pueden ser: collection, limit, orderBy, where, operador... 
 * Por ejemplo el tipo de consulta puede ser: collection, query...
 * @param {*} params = parametros necesarios para realizar las distintas consultas a Firestore según el tipo de consulta a realizar 
 * @returns Array con el resultado, ya sea un elemento (document) o varios
 */
const getData = async (params) => {
    try {
        const { coll, lim = null, start = null, ord = null, wee = null } = params;
        let q = query(getRef(coll));
        if (ord) q = query(q, orderBy(ord));
        if (start) q = query(q, startAfter(start));
        if (lim) q = query(q, limit(lim));

        for (var i = 0; q && i <= wee?.length - 3; i = i + 3) { // i + 3
            const field = wee[i];
            const operator = wee[i + 1];
            const value = wee[i + 2];

            if (field && operator && value) {
                q = query(q, where(field, operator, value));
            }
        }

        let ret = [];
        // queremos que sea == y no ===. Está hecho a propósito
        // eslint-disable-next-line eqeqeq
        if (q.type == 'query') {
            const snapshot = await getDocs(q);
            snapshot.forEach(snap => {
                ret.push(snap.data());
            })
            // eslint-disable-next-line eqeqeq
        } else if (q.type == 'collection') {
            const snap = await getDocs(q);
            snap.forEach(snap => {
                ret.push(snap.data());
            })
        } else {
            const snap = await getDoc(q);
            ret.push(snap.data());
        }
        return ret;

    } catch (error) {
        console.log('Error en getDATA de firebaseConfig', error)
    }
}

/**
 * Permite guardar en Firestore según los parámetros recibidos.
 * Por ejemplo, los parámetros suelen ser: collection/path, objeto y si se trata de una actualización o una creación 
 * @param {*} params = parametros que son necesarios para crear o actualizar un documento en Firestore
 * @returns true si se realiza correctamente y null si no se realiza la creacion/actualizacion
 */
const saveData = async (params) => {
    let ret = null;
    try {
        const { coll, data, update = true } = params;
        const ref = getRef(coll, true);
        if (ref) {
            if (update) { await updateDoc(ref, data); }
            else { await setDoc(ref, data); }
            ret = true;
        }
    } catch (error) {
        console.log(error);
    }
    return ret;
}

/**
 * Permite borrar en Firestore según los parámetros recibidos.
 * Por ejemplo, los parámetros pueden ser: collection/path,
 * @param {*} params = parametros que son necesarios para borrar un documento en Firestore
 * @returns true si se realiza correctamente y null si no se realiza el borrado
 */
const deleteData = async (params) => {
    let ret = null;
    try {
        const { coll } = params;
        const ref = getRef(coll, true);
        if (ref) {
            await deleteDoc(ref);
            ret = true;
        }
    } catch (error) {
        console.log(error);
    }
    return ret;
}

/**
 * Obtiene la referencia a un documento o collection de Firestore
 * @param {*} path  = el path al documento en cuestion
 * @param {*} toDoc = para saber si es ruta a documento o a collection
 * @returns la referncia obtenida
 */
const getRef = (path, toDoc = false) => {
    try {
        const a = (typeof path == 'string') ? path.split('/') : path;
        // eslint-disable-next-line eqeqeq
        if (toDoc && a.length % 2 != 0) {
            console.log(`Es una ruta hasta documento y no es par`);
            return null;
        }
        let ref = getFirestore(app);
        for (var i in a) {
            // eslint-disable-next-line eqeqeq
            if (i % 2 == 0) { ref = collection(ref, a[i]); }
            else { ref = doc(ref, a[i]); }
        }
        return ref;
    } catch (error) {
        console.log(error);
        return null
    }
}

/**
 * Carga un fichero en Storage, a una ruta especificada, borrando antes los elementos que contenga el directorio en cuestión para no crear conflicto si se trata de distintas extensiones de fichero. Despues se realiza la carga del fichero deseado.
 * @param {String} path  = la ruta a la que subir el fichero (hasta la carpeta contenedora)
 * @param {*} f     = el fichero a subir
 * @param {String} name  = nombre con el que se subirá el fichero
 * @returns null, porque la respuesta no se maneja
 */
const uploadFile = async (path, f, name = 'logo') => {
    // subimos fichero a Storage. como se permiten varias extensiones de logo, tenemos que vaciar el directorio de Storage primero
    // asi nos aseguramos que no se quedan varios logos de distinta extension en Storage, pq crearía conflicto en la web de qué mostrar. 
    let storageRef = null;
    try {
        const metadata = {
            contentType: f.type,
            size: f.size
        };
        const ext = f.name.match(/[^.]+$/)[0];
        const storage = getStorage();

        // primero vaciamos el directorio path de los posibles logos que haya
        let directoryRef = ref(storage, path);
        const fileList = await listAll(directoryRef);
        await Promise.all(fileList.items.map((fileRef) => deleteObject(fileRef)));

        // despues actualizamos el directorio con el logo correcto
        storageRef = ref(storage, `${path}/${name}.${ext}`, metadata);
        // eslint-disable-next-line no-unused-vars
        const res = await uploadBytes(storageRef, f);
    } catch (e) {
        console.log(e)
    }

    try {
        const pepe = await getDownloadURL(storageRef)
        return pepe;
    } catch (e) { }

    return null;
}

/**
 * Borra un directorio y todos sus elementos del Storage. Se localiza el directorio o carpeta contenedora según el path indicado y se elimina dicho directorio y sus elementos.
 * @param {*} path = directorio a borrar
 * @returns null, porque la respuesta no se maneja
 */
const deleteFolder = async (path) => {
    // borramos todos los logos que haya en el directorio que pasamos como path, hasta id_concesionario
    // es decir, aqui borramos el directorio completo, el id_concesionario + todo lo que contiene
    try {
        const storage = getStorage();
        const listRef = ref(storage, path);
        // Find all the prefixes and items.
        listAll(listRef)
            .then((res) => {
                res.prefixes.forEach((folderRef) => {
                    // All the prefixes under listRef.
                    // You may call listAll() recursively on them.
                    // console.log('prefixex que imagino es para subdirectorio')
                });
                res.items.forEach(async (itemRef) => {
                    await deleteObject(itemRef);
                    // All the items under listRef.
                });
            }).catch((error) => {
                // Uh-oh, an error occurred!
                console.log('Uh-oh, an error occurred: ', error)
            });
    } catch (e) {
        console.log(e)
    }
    return null;
}

/**
 * Obtiene la url de un fichero del Storage, en este caso se trata de imagenes. 
 * @param {*} path = path o directorio del recurso que buscamos
 * @param {*} img  = nombre del recurso que buscamos
 * @returns la url si localiza el recurso, y null si no lo hace
 */
const getImageUrl = async (path, img = 'logo') => {
    try {
        const exts = ['jpg', 'png', 'jpeg'];
        let pepe = null;
        const storage = getStorage();
        for (var i = 0; !pepe && i < exts.length; i++) {
            const storageRef = ref(storage, `${path}/${img}.${exts[i]}`);
            try {
                pepe = await getDownloadURL(storageRef)
                // eslint-disable-next-line eqeqeq
                if (path == '85') console.log(pepe)
            } catch (e) { }
        }
        return pepe;
    } catch (e) { }
    return null;
}

export {
    app,
    initializeApp,
    signInWithEmailAndPassword,
    getFirestore,
    getAuth,
    logOut,
    getData,
    saveData,
    deleteData,
    uploadFile,
    getImageUrl,
    getBearer,
    pathPart,
    deleteFolder,
}